// @korvo-app-router-pattern: AL
'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useState } from 'react';

import { shellConfig } from '@/config/shell-config';
import { normalizeError } from '@/lib/normalize-error';

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

type SignupExtraFieldDef = {
  key: string;
  label: string;
  type: 'text' | 'number' | 'checkbox';
  required?: boolean;
};

type RegistrationPageSettings = {
  logoUrl?: string;
  heading?: string;
  subtitle?: string;
  customInstructions?: string;
  /** US-12.12: extra fields to collect at signup */
  extraFields?: SignupExtraFieldDef[] | null;
};

function getTextInputValue(value: string | number | boolean | undefined): string {
  return typeof value === 'string' ? value : '';
}

function getNumberInputValue(value: string | number | boolean | undefined): number | '' {
  return typeof value === 'number' ? value : '';
}

function RegisterForm() {
  const config = shellConfig;
  const searchParams = useSearchParams();
  const _invite = searchParams?.get('invite')?.trim();
  const inviteToken = _invite != null ? _invite : '';
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const _codeParam = searchParams?.get('code');
  const initialRegistrationCode = (_codeParam != null && _codeParam !== '') ? _codeParam : '';
  const [registrationCode, setRegistrationCode] = useState(initialRegistrationCode);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [pageSettings, setPageSettings] = useState<RegistrationPageSettings | null>(null);
  const [inviterName, setInviterName] = useState<string | null>(null);
  const [extraValues, setExtraValues] = useState<Record<string, string | number | boolean | undefined>>({});

  useEffect(() => {
    let cancelled = false;
    fetch('/api/auth/registration-page-settings')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: RegistrationPageSettings | null) => {
        if (!cancelled && data) setPageSettings(data);
      })
      .catch(() => {
        if (!cancelled) setPageSettings({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // US-12.8: Prefill email (and show inviter) when arriving via invite link
  useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    fetch(`/api/auth/accept-invite?token=${encodeURIComponent(inviteToken)}`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { email?: string; inviterName?: string } | null) => {
        if (!cancelled && data?.email) {
          setEmail(data.email);
          if (data.inviterName) setInviterName(data.inviterName);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    if (!email || !password) {
      setError('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    const extraFields = pageSettings?.extraFields ?? [];
    for (const field of extraFields) {
      if (field.required) {
        const v = extraValues[field.key];
        const isEmpty =
          v === undefined ||
          v === '' ||
          (field.type === 'number' && (typeof v !== 'number' || Number.isNaN(v)));
        if (isEmpty) {
          setError(`Please fill in: ${field.label}`);
          setIsLoading(false);
          return;
        }
      }
    }

    const extraPayload =
      Object.keys(extraValues).length > 0
        ? (Object.fromEntries(
            Object.entries(extraValues).filter(
              ([, v]) => v !== '' && v !== undefined
            )
          ) as Record<string, string | number | boolean>)
        : undefined;
    const extraToSend =
      extraPayload && Object.keys(extraPayload).length > 0 ? extraPayload : undefined;

    try {
      const response = await fetch('/api/auth/sign-up-with-code', {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          name: name || undefined,
          registrationCode: registrationCode || undefined,
          inviteToken: inviteToken || undefined,
          extra: extraToSend,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data.message || data.error || 'Registration failed. Please try again.';
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      window.location.href = config.routes.signIn;
    } catch (err: unknown) {
      console.error('[Register] Registration exception:', normalizeError(err));
      setError('An error occurred during registration. Please try again.');
      setIsLoading(false);
    }
  };

  const heading = pageSettings?.heading ?? 'Create your account';
  const subtitle =
    pageSettings?.subtitle ??
    (config.branding.brandingText || `Register for ${config.branding.appName}`);
  const customInstructions = pageSettings?.customInstructions;
  const logoUrl = pageSettings?.logoUrl;

  return (
    <div
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      style={{ paddingBottom: 'calc(var(--footer-height, 5rem) + 3rem)' }}
    >
      <div className="min-h-[60vh] flex items-center justify-center py-12">
        <div className="max-w-md w-full">
          <div
            className="rounded-xl border shadow-sm p-8 space-y-6"
            style={{
              borderColor: 'var(--input-border)',
              backgroundColor: 'var(--card-background)',
            }}
          >
            <div className="text-center space-y-3">
              {logoUrl && (
                <div className="flex justify-center" aria-hidden>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={logoUrl}
                    alt=""
                    width={80}
                    height={80}
                    className="object-contain max-h-20 w-auto"
                  />
                </div>
              )}
              <h2
                className="text-2xl sm:text-3xl font-extrabold"
                style={{ color: 'var(--foreground-default)' }}
              >
                {heading}
              </h2>
              <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
                {subtitle}
              </p>
              {inviterName && (
                <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
                  You were invited by {inviterName}.
                </p>
              )}
              {customInstructions && (
                <div
                  className="text-left text-sm rounded-md px-3 py-2"
                  style={{
                    color: 'var(--foreground-muted)',
                    backgroundColor: 'var(--background-muted)',
                  }}
                  role="region"
                  aria-label="Instructions for invitees"
                >
                  {customInstructions}
                </div>
              )}
            </div>
            <form className="space-y-5" onSubmit={(e) => void submit(e)}>
              {error && (
                <div
                  className="px-4 py-3 rounded-md border"
                  style={{
                    backgroundColor: 'var(--danger-default)',
                    borderColor: 'var(--danger-default)',
                    color: 'var(--danger-foreground)',
                  }}
                >
                  {error}
                </div>
              )}
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium"
                    style={{ color: 'var(--label-foreground)' }}
                  >
                    Name
                  </label>
                  <input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                    style={{
                      borderColor: 'var(--input-border)',
                      backgroundColor: 'var(--input-background)',
                      color: 'var(--input-foreground)',
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium"
                    style={{ color: 'var(--label-foreground)' }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                    style={{
                      borderColor: 'var(--input-border)',
                      backgroundColor: 'var(--input-background)',
                      color: 'var(--input-foreground)',
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium"
                    style={{ color: 'var(--label-foreground)' }}
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                    style={{
                      borderColor: 'var(--input-border)',
                      backgroundColor: 'var(--input-background)',
                      color: 'var(--input-foreground)',
                    }}
                  />
                </div>
                <div>
                  <label
                    htmlFor="registrationCode"
                    className="block text-sm font-medium"
                    style={{ color: 'var(--label-foreground)' }}
                  >
                    Registration code
                  </label>
                  <input
                    id="registrationCode"
                    value={registrationCode}
                    onChange={(e) => setRegistrationCode(e.target.value)}
                    placeholder="Required if invite-only is enabled"
                    className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                    style={{
                      borderColor: 'var(--input-border)',
                      backgroundColor: 'var(--input-background)',
                      color: 'var(--input-foreground)',
                    }}
                  />
                </div>
                {pageSettings?.extraFields?.map((field) => (
                  <div key={field.key}>
                    <label
                      htmlFor={`extra-${field.key}`}
                      className="block text-sm font-medium"
                      style={{ color: 'var(--label-foreground)' }}
                    >
                      {field.label}
                      {field.required && ' *'}
                    </label>
                    {field.type === 'checkbox' ? (
                      <input
                        id={`extra-${field.key}`}
                        type="checkbox"
                        checked={Boolean(extraValues[field.key])}
                        onChange={(e) =>
                          setExtraValues((prev) => ({ ...prev, [field.key]: e.target.checked }))
                        }
                        className="mt-1 block rounded focus:ring-2 focus:ring-primary-default sm:text-sm"
                      />
                    ) : field.type === 'number' ? (
                      <input
                        id={`extra-${field.key}`}
                        type="number"
                        value={getNumberInputValue(extraValues[field.key])}
                        onChange={(e) => {
                          const v = e.target.value;
                          setExtraValues((prev) => ({
                            ...prev,
                            [field.key]: v === '' ? 0 : Number(v),
                          }));
                        }}
                        required={field.required}
                        className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                        style={{
                          borderColor: 'var(--input-border)',
                          backgroundColor: 'var(--input-background)',
                          color: 'var(--input-foreground)',
                        }}
                      />
                    ) : (
                      <input
                        id={`extra-${field.key}`}
                        type="text"
                        value={getTextInputValue(extraValues[field.key])}
                        onChange={(e) =>
                          setExtraValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        required={field.required}
                        className="mt-1 block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default sm:text-sm"
                        style={{
                          borderColor: 'var(--input-border)',
                          backgroundColor: 'var(--input-background)',
                          color: 'var(--input-foreground)',
                        }}
                      />
                    )}
                  </div>
                ))}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={cn(
                    'w-full py-3 px-4 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors'
                  )}
                  style={{
                    backgroundColor: 'var(--primary-default)',
                    color: 'var(--primary-foreground)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading)
                      e.currentTarget.style.backgroundColor = 'var(--primary-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary-default)';
                  }}
                >
                  {isLoading ? 'Creating...' : 'Create Account'}
                </button>
              </div>
              <div className="text-center pt-2">
                <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
                  Already have an account?{' '}
                  <Link
                    href={config.routes.signIn}
                    className="hover:underline"
                    style={{ color: 'var(--link-default)' }}
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function RegisterFallback() {
  const config = shellConfig;
  return (
    <div
      className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
      style={{ paddingBottom: 'calc(var(--footer-height, 5rem) + 3rem)' }}
    >
      <div className="min-h-[60vh] flex items-center justify-center py-12">
        <div className="max-w-md w-full">
          <div
            className="rounded-xl border shadow-sm p-8"
            style={{
              borderColor: 'var(--input-border)',
              backgroundColor: 'var(--card-background)',
            }}
          >
            <h2
              className="text-center text-2xl font-extrabold"
              style={{ color: 'var(--foreground-default)' }}
            >
              Create your account
            </h2>
            <p className="mt-2 text-center text-sm" style={{ color: 'var(--foreground-muted)' }}>
              {config.branding.brandingText || `Register for ${config.branding.appName}`}
            </p>
            <div className="mt-8 flex justify-center" style={{ color: 'var(--foreground-muted)' }}>
              Loading…
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={<RegisterFallback />}>
      <RegisterForm />
    </Suspense>
  );
}

